import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { ApiError, apiJson } from '@/lib/apiClient';

/**
 * SLICE 14d — the tenant's people, for the command-PIN picker.
 *
 * Replaces the typed user uuid Mahad hit ("it asks me user ID which is long and
 * i dont know"). Mirrors the wire contract in `api/src/modules/admin/routes.ts`
 * ("SLICE 14d"):
 *   GET /api/admin/tenants/:tenantId/users
 *     200 → { tenantId, tenantName, users: [{ id, name|null, phone, role,
 *                                             disabledAt, hasCommandPin }] }
 *
 * `requireSuperAdmin` on the server (it carries customers' phone numbers), so the
 * query is gated by `enabled` rather than fired-and-403'd.
 */
export interface TenantUser {
  id: string;
  name: string | null;
  phone: string;
  role: 'owner' | 'manager' | 'viewer';
  /** ISO instant the account was disabled, or null. Listed and LABELLED, never
   *  hidden — a picker that silently omits a person the operator knows exists is
   *  how they conclude provisioning is broken. */
  disabledAt: string | null;
  /** Whether there is a PIN to clear — never what it is. Lets the operator see
   *  there is nothing to clear before clearing it. */
  hasCommandPin: boolean;
}

export interface TenantUsers {
  tenantId: string;
  tenantName: string;
  users: TenantUser[];
}

export const tenantUsersQueryKey = (tenantId: string) =>
  ['admin', 'tenants', tenantId, 'users'] as const;

export function useTenantUsers(
  tenantId: string,
  enabled = true,
): UseQueryResult<TenantUsers> {
  return useQuery({
    queryKey: tenantUsersQueryKey(tenantId),
    enabled,
    queryFn: ({ signal }) =>
      apiJson<TenantUsers>(
        `/api/admin/tenants/${encodeURIComponent(tenantId)}/users`,
        { signal },
      ),
    // A 4xx is a decision, not a hiccup — never retried.
    retry: (count, error) =>
      !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
  });
}
